import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AITaskProcessingDialog } from '@/components/ai/ai-task-processing-dialog';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { Document } from '@/types/document';

// Define DeletedDocumentRevision interface
interface DeletedDocumentRevision {
  id: string;
  data: Record<string, any>;
  deletedAt: any;
  originalDocId?: string;
}

import { toast } from 'sonner';
import { notificationService, notify } from '@/services/notificationService';
// ahhhhhhhhhhhhhh duck
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
// import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTable } from '@/components/ui/datatable';
import { documentLibraryColumnConfig } from '@/config/tableColumnConfig';
import { getDocumentColumns } from './components/columns';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit, Eye, Upload, Download, Search, ChevronDown, FileEdit, Loader2, FileText, Trash2, History, Undo, Share2, Brain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import './document-register.css';

import { BulkEditSheet } from './components/bulk-edit-sheet';
import { withAutoStatus } from '@/utils/documentStatus';
import { useNavigate } from 'react-router-dom';
import { cleanDateAndTimeObject, renderDate } from '@/../shared/renderDate';
import documentsService, { getDocuments, getDocument, saveDocument, recoverDocument, getDeletedDocuments } from '@/services/documentsService';
import { db } from '@/js/external-js/config/main_config';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { routePathByName } from '@/app/routes';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { FilePickerDialog } from '@/app/pages/policy-admin/components/FilePickerDialog';
import { IntegrationSlot } from '@/components/integrations/IntegrationSlot';
import { createDocument } from '@/services/documentsService';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { TourAlertDialog, useTour } from '@/components/tour';

// Define valid badge variants as a const array for type safety
const BADGE_VARIANTS = [
  'active', 'inactive', 'deleted', 'secondary', 'policy', 'procedure',
  'form', 'report', 'record', 'manual', 'plan', 'guideline', 'standard',
  'checklist', 'matrix', 'presentation', 'pdf', 'evidence', 'image',
  'spreadsheet', 'database', 'code', 'website', 'template', 'hardware',
  'software', 'service', 'personnel', 'documentation', 'other'
] as const;

type BadgeVariant = typeof BADGE_VARIANTS[number];

// Utility to get document type variant for Badge component
const getDocTypeVariant = (type: string): BadgeVariant => {
  if (!type) return 'secondary';

  const lowerType = type.toLowerCase();
  const validDocVariants = [
    'policy', 'procedure', 'form', 'report', 'record', 'manual', 'plan',
    'guideline', 'standard', 'checklist', 'matrix', 'presentation', 'pdf',
    'evidence', 'image', 'spreadsheet', 'database', 'code', 'website',
    'template', 'hardware', 'software', 'service', 'personnel', 'documentation', 'other'
  ] as const;

  return validDocVariants.includes(lowerType as any) ? lowerType as BadgeVariant : 'secondary';
};

// Utility to get status variant for Badge component
export const getStatusVariant = (status: string): BadgeVariant => {
  if (!status) return 'secondary';

  const lowerStatus = status.toLowerCase();
  if (['current', 'published'].includes(lowerStatus)) {
    return 'active';
  } else if (lowerStatus === 'draft') {
    return 'inactive';
  } else if (['expired', 'overdue', 'deleted'].includes(lowerStatus)) {
    return 'deleted';
  }

  console.log('[DocumentLibrary] Unknown status:', lowerStatus);
  return 'secondary';
};

export const convertStatus = (status: string, nextReviewDate?: Date): string => {
  // console.log('[DocumentLibrary] Converting status:', status);
  if (!status) {
    return 'Draft';
  }
  if (nextReviewDate) {
    const nextReviewDateObj = nextReviewDate;
    const today = new Date();
    if (nextReviewDateObj < today) {
      // console.log('[DocumentLibrary] Document is overdue', {
      //   status,
      //   nextReviewDate,
      //   nextReviewDateObj,
      //   today
      // });
      return 'Overdue';
    } else {
      // console.log('[DocumentLibrary] Document is not overdue', {
      //   status,
      //   nextReviewDate,
      //   nextReviewDateObj,
      //   today
      // });
      return 'Published';
    }
  }
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === 'current' || lowerStatus === 'published') {
    // console.log('[DocumentLibrary] Converted to Published');
    return 'Published';
  } else if (lowerStatus === 'draft') {
    // console.log('[DocumentLibrary] Converted to Draft');
    return 'Draft';
  } else if (lowerStatus === 'expired') {
    // console.log('[DocumentLibrary] Converted to Expired');
    return 'Overdue';
  } else if (lowerStatus === 'overdue') {
    // console.log('[DocumentLibrary] Converted to Overdue');
    return 'Overdue';
  } else if (lowerStatus === 'deleted') {
    // console.log('[DocumentLibrary] Converted to Deleted');
    return 'Deleted';
  } else {
    // console.log('[DocumentLibrary] Converted to Unknown');
    return 'Unknown';
  }
};

// Define view types
type DocumentView = 'active' | 'deleted';

interface DocumentLibraryProps {
  isReadOnly?: boolean;
  onBackNavigation?: () => void;
}

const DocumentLibrary: React.FC<DocumentLibraryProps> = ({
  isReadOnly = false,
  onBackNavigation
}) => {
  const navigate = useNavigate();
  const { activeTenancy, users, userIdToName } = useTenancy();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { features } = useFeatureFlags();
  
  // Session storage key for persisting filters
  const FILTERS_STORAGE_KEY = 'documentLibrary_filters';
  
  // Load filters from sessionStorage
  const loadFiltersFromStorage = () => {
    try {
      const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading filters from sessionStorage:', e);
    }
    return null;
  };
  
  const storedFilters = loadFiltersFromStorage();
  
  // Initialize state from sessionStorage to persist filters across navigation
  const [searchTerm, setSearchTerm] = useState(storedFilters?.search || '');

  // Input with icon component
  const InputWithIcon = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { icon: React.ReactNode }>(
    ({ className, icon, ...props }, ref) => {
      return (
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
          <Input
            className={`pl-10 ${className || ''}`}
            ref={ref}
            {...props}
          />
        </div>
      );
    }
  );
  InputWithIcon.displayName = 'InputWithIcon';
  
  // Initialize filters from sessionStorage (only on mount)
  const [typeFilter, setTypeFilter] = useState<'all' | string>(storedFilters?.type || 'all');
  const [statusFilter, setStatusFilter] = useState<'all' | string>(storedFilters?.status || 'all');
  const [ownerFilter, setOwnerFilter] = useState<'all' | string>(storedFilters?.owner || 'all');
  const [view, setView] = useState<DocumentView>(storedFilters?.view || 'active');
  // pageIndex is 0-based
  const [pageIndex, setPageIndex] = useState<number>(storedFilters?.pageIndex || 0);
  
  // Save filters to sessionStorage when they change
  useEffect(() => {
    const filters = {
      search: searchTerm,
      type: typeFilter,
      status: statusFilter,
      owner: ownerFilter,
      view: view,
      pageIndex: pageIndex
    };
    sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [searchTerm, typeFilter, statusFilter, ownerFilter, view, pageIndex]);
  const [deletedDocuments, setDeletedDocuments] = useState<any[]>([]);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [recoveringDocId, setRecoveringDocId] = useState<string | null>(null);
  const [showRecoverDialog, setShowRecoverDialog] = useState(false);
  const [selectedDocToRecover, setSelectedDocToRecover] = useState<any>(null);

  const [helpContent, setHelpContent] = useState(null);

  // Bulk edit state
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);

  // File Picker Dialog State (for when file not found during download)
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [filePickerFiles, setFilePickerFiles] = useState<Array<{ name: string; fullPath: string; size?: number; updated?: Date }>>([]);
  const [filePickerDocName, setFilePickerDocName] = useState('');
  const [filePickerDocId, setFilePickerDocId] = useState('');
  const [filePickerRow, setFilePickerRow] = useState<any>(null);
  const [filePickerLoading, setFilePickerLoading] = useState(false);
  // State for SharePoint integration
  const [isSharePointOpen, setIsSharePointOpen] = useState(false);
  const [sharePointSites, setSharePointSites] = useState<any[]>([]);
  const [sharePointDrives, setSharePointDrives] = useState<any[]>([]);
  const [sharePointFiles, setSharePointFiles] = useState<any[]>([]);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [selectedDrive, setSelectedDrive] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isLoadingDrives, setIsLoadingDrives] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  // State for single document editing
  const [documentToEdit, setDocumentToEdit] = useState<Document | null>(null);

  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  // AI Task generation state
  const [processingAITask, setProcessingAITask] = useState<string | null>(null);

  // Force refresh of mock documents to get the latest data with componentId and classification
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load deleted documents
  const loadDeletedDocuments = useCallback(async () => {
    if (!activeTenancy?.id) return;

    try {
      setIsLoadingDeleted(true);
      console.log('[DocumentLibrary] Loading deleted documents for tenancy:', activeTenancy.id);

      const deletedDocs = await getDeletedDocuments(activeTenancy.id);
      // Map the raw data to our DeletedDocumentRevision type
      const mappedDocs: DeletedDocumentRevision[] = deletedDocs.map(doc => ({
        id: doc.id,
        data: doc.data || {},
        deletedAt: doc.deletedAt,
        deletedBy: doc.deletedBy,
        originalDocId: doc.originalDocId || doc.data?.docID || 'unknown',
        fileName: doc.originalFileName || 'unknown',
        originalFileName: doc.originalFileName || doc.data?.fileName || 'unknown',
        documentType: doc.data?.documentType,
        status: "Deleted",
        classification: doc.data?.classification,
        dateUploaded: doc.data?.dateUploaded,
        uploadedBy: doc.data?.uploadedBy
      }));

      setDeletedDocuments(mappedDocs);
      console.log(`[DocumentLibrary] Loaded ${mappedDocs.length} deleted documents`);
    } catch (error) {
      console.error('[DocumentLibrary] Error loading deleted documents:', error);
      toast.error('Failed to load deleted documents: ' + error.message);
    } finally {
      setIsLoadingDeleted(false);
    }
  }, [activeTenancy?.id]);

  // Handle document recovery
  // const handleRecoverDocument = async (revisionId: string, newDocId: string | null = null) => {
  //   if (!revisionId) return;

  //   setRecoveringDocId(revisionId);
  //   try {
  //     await recoverDocument(revisionId, newDocId, activeTenancy?.id || undefined);
  //     toast.success('Document recovered successfully');

  //     // Refresh both active and deleted documents
  //     loadDocuments();
  //     loadDeletedDocuments();
  //   } catch (error) {
  //     console.error('[DocumentLibrary] Error recovering document:', error);
  //     toast.error(`Failed to recover document: ${error.message}`);
  //   } finally {
  //     setRecoveringDocId(null);
  //   }
  // };

  // Load documents function
  const loadDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      // Always prioritize activeTenancy.id over window.companyID
      const tenancyId = activeTenancy?.id || window.companyID;
      console.log('[DocumentLibrary] Loading documents for tenancy:', tenancyId);

      if (!tenancyId || tenancyId === '' || tenancyId === 'Loading...') {
        console.log('[DocumentLibrary] No valid tenancy ID, skipping document load');
        setDocuments([]);
        return;
      }

      // Clear window.companyID to ensure we use the tenancy ID
      const prevCompanyId = window.companyID;
      window.companyID = undefined;

      let docs = await getDocuments(tenancyId);

      // Restore window.companyID
      window.companyID = prevCompanyId;

      console.log('[DocumentLibrary] Loaded documents:', docs?.length || 0);

      // Deduplicate documents by ID to prevent duplicates in the table
      if (docs && Array.isArray(docs)) {
        // Create a Map to store unique documents by ID
        const uniqueDocsMap = new Map();

        // Add each document to the map, with the ID as the key
        // This will automatically overwrite duplicates
        docs.forEach(doc => {
          if (doc && doc.docID) {
            uniqueDocsMap.set(doc.docID, doc);
          }
        });

        // Convert the Map back to an array
        docs = Array.from(uniqueDocsMap.values());
        console.log('[DocumentLibrary] Deduplicated documents:', docs.length);
      }

      console.log('[DocumentLibrary] Final documents:', docs?.length || 0, docs)
      const docsWithStatus = docs.map(doc => {
        console.log('[DocumentLibrary] Document:', doc)
        // Ensure we have all required fields with defaults
        const baseDoc = {
          id: doc.docID || '',
          docID: doc.docID || '',
          status: convertStatus(doc.status, cleanDateAndTimeObject(doc.nextReviewDate)),
          documentType: doc.documentType || 'Policy',
          title: (doc as any).displayName || doc.title || doc.fileName || '',
          description: doc.description || '',
          fileName: doc.fileName || '',
          uploadedBy: doc.uploadedBy || '',
          owner: doc.owner || '',
          dateAdded: doc.dateAdded || '',
          classification: doc.classification || ''
        };
        return withAutoStatus({ ...doc, ...baseDoc });
      });
      setDocuments(docsWithStatus as unknown as Document[]);
    } catch (error) {
      console.error('[DocumentLibrary] Error loading documents:', error);
      setDocuments([]);
      toast.error(`Failed to load documents: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTenancy?.id, setIsLoading, setDocuments]);

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [currentProcessingDoc, setCurrentProcessingDoc] = useState<{ id: string; fileName: string } | null>(null);

  const handleGenerateAITasks = async (document: Document) => {
    if (!document || !activeTenancy?.id) {
      await notify.error('Document Error', 'Document information is incomplete');
      toast.error('Document information is incomplete');
      return;
    }

    // Use type assertion to safely access properties that might not be in the union type
    const docId = (document as any).docID || (document as any).id;
    if (!docId) {
      await notify.error('Document Error', 'Document ID not found');
      toast.error('Document ID not found');
      return;
    }

    const storageFileName = (document as any).fileName;
    const displayName = (document as any).displayName || storageFileName;
    if (!storageFileName) {
      await notify.error('Document Error', 'Document filename not found');
      toast.error('Document filename not found');
      return;
    }

    setProcessingAITask(docId);

    try {
      // Construct a path based on customer ID and document ID
      const filePath = `customers/${activeTenancy.id}/keyDocuments/otherDocs/uploaded/${docId}/${storageFileName}`;

      // Make API call to send SQS message for AI processing
      const response = await fetch('https://aiapi.deit.dev/api/sqs/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user?.getIdToken()}`
        },
        body: JSON.stringify({
          customerId: activeTenancy.id,
          documentId: docId,
          documentPath: filePath,
          fileName: storageFileName,
          processingType: "assurance_task_analysis",
          initiatedBy: user?.uid
        })
      });

      const result = await response.json();
      console.log('SQS message response:', result);

      if (result.success) {
        await notify.success(
          '🎯 AI Task Processing Started!',
          `Your document "${displayName}" has been queued for AI processing. You'll receive a notification when tasks are ready.`,
          true,
          user?.uid,
          "/system-update"
        );
        toast.success(`Your document "${displayName}" has been queued for AI processing. You'll receive a notification when tasks are ready.`);
      } else {
        throw new Error(result.error || 'Failed to queue document for processing');
      }
    } catch (error) {
      console.error('Error generating AI tasks:', error);
      await notify.error('AI Task Generation Failed', `Failed to generate AI tasks: ${error instanceof Error ? error.message : 'Unknown error'}`, true, user?.uid);
      toast.error(`Failed to generate AI tasks: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingAITask(null);
    }
  };

  const handleRecoverDocument = async (document: Document | DeletedDocumentRevision) => {
    if (!document.id) {
      console.error('Cannot recover document: missing revision ID');
      toast.error('Cannot recover document: missing revision ID');
      return;
    }

    try {
      setRecoveringDocId(document.id as string);
      // Pass the document ID (which is the revision ID) and the originalDocId as the newDocId
      await recoverDocument(document.id as string, 'originalDocId' in document ? document.originalDocId as string : undefined, activeTenancy?.id || undefined);
      toast.success('Document recovered successfully');

      // Refresh both active and deleted documents
      await loadDocuments();
      await loadDeletedDocuments();
    } catch (error) {
      console.error('Error recovering document:', error);
      toast.error(`Failed to recover document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setRecoveringDocId(null);
    }
  };

  // Handle file selection from file picker dialog (when file not found during download)
  const handleFilePickerSelect = async (
    file: { name: string; fullPath: string; size?: number; updated?: Date },
    fixLinkage: boolean
  ) => {
    const loadingToast = toast.loading('Downloading file...');
    try {
      const { ref, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('@/lib/firebase');
      
      const fileRef = ref(storage, file.fullPath);
      const downloadUrl = await getDownloadURL(fileRef);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.dismiss(loadingToast);
      toast.success('Download started');
      
      // Fix linkage if requested
      console.log('[DocumentLibrary] handleFilePickerSelect - fixLinkage:', fixLinkage, 'filePickerRow:', filePickerRow, 'filePickerDocId:', filePickerDocId);
      
      if (fixLinkage && filePickerRow && filePickerDocId) {
        const fixToast = toast.loading('Updating document file reference...');
        try {
          // Update the supporting doc in document library
          const docPath = `customers/${activeTenancy?.id}/product/policyPackages/ISO27001keyDocuments/otherDocs`;
          console.log('[DocumentLibrary] Updating document library at:', docPath, 'docId:', filePickerDocId);
          
          const docRef = db.collection('customers').doc(activeTenancy?.id)
            .collection('product').doc('policyPackages')
            .collection('ISO27001keyDocuments').doc('otherDocs');
          
          await docRef.update({
            [`uploaded.${filePickerDocId}.fileName`]: file.name,
            [`uploaded.${filePickerDocId}.file_name`]: file.name,
          });
          console.log('[DocumentLibrary] Document library updated successfully');
          
          toast.dismiss(fixToast);
          toast.success('Document file reference updated');
          
          // Refresh the document list to reflect the change
          await loadDocuments();
        } catch (updateError) {
          console.error('[DocumentLibrary] Error updating file reference:', updateError);
          toast.dismiss(fixToast);
          toast.error('Downloaded but failed to update file reference');
        }
      } else {
        console.log('[DocumentLibrary] Skipping linkage fix - conditions not met');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('[DocumentLibrary] Error downloading selected file:', error);
      toast.error('Failed to download selected file');
      throw error;
    }
  };

  // Check SharePoint authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const status = await documentsService.getSharePointAuthStatus();
        setIsAuthenticated(status.isAuthenticated);
        if (status.isAuthenticated && isSharePointOpen) {
          loadSharePointSites();
        }
      } catch (error) {
        console.error('Error checking SharePoint auth status:', error);
      }
    };

    if (isSharePointOpen) {
      checkAuthStatus();
    }
  }, [isSharePointOpen]);

  // Migration: Convert "Evidence" documentType to "Task Evidence" (runs once per tenancy)
  const migrateEvidenceToTaskEvidence = useCallback(async (tenancyId: string) => {
    const MIGRATION_KEY = 'evidence_to_task_evidence_migration';
    
    try {
      // Check if migration has already been done for this tenancy
      const migrationDoc = await db.doc(`/customers/${tenancyId}/migrations/${MIGRATION_KEY}`).get();
      
      if (migrationDoc.exists) {
        console.log('[DocumentLibrary] Evidence to Task Evidence migration already completed for this tenancy');
        return;
      }
      
      console.log('[DocumentLibrary] Running Evidence to Task Evidence migration...');
      
      // Get the otherDocs document
      const otherDocsRef = db.doc(`/customers/${tenancyId}/product/policyPackages/ISO27001keyDocuments/otherDocs`);
      const otherDocsSnap = await otherDocsRef.get();
      
      if (!otherDocsSnap.exists) {
        console.log('[DocumentLibrary] No otherDocs found, skipping migration');
        // Mark migration as done anyway
        await db.doc(`/customers/${tenancyId}/migrations/${MIGRATION_KEY}`).set({
          completedAt: new Date(),
          documentsUpdated: 0,
          status: 'completed_no_docs'
        });
        return;
      }
      
      const data = otherDocsSnap.data();
      const uploaded = data?.uploaded || {};
      
      // Find all documents with documentType === "Evidence"
      const updates: Record<string, any> = {};
      let updatedCount = 0;
      
      for (const [docId, docData] of Object.entries(uploaded)) {
        const doc = docData as any;
        if (doc?.documentType?.toLowerCase() === 'evidence') {
          updates[`uploaded.${docId}.documentType`] = 'Task Evidence';
          updatedCount++;
        }
      }
      
      if (updatedCount > 0) {
        // Apply all updates in a single batch
        await otherDocsRef.update(updates);
        console.log(`[DocumentLibrary] Migrated ${updatedCount} documents from "Evidence" to "Task Evidence"`);
      } else {
        console.log('[DocumentLibrary] No documents with "Evidence" type found');
      }
      
      // Mark migration as complete
      await db.doc(`/customers/${tenancyId}/migrations/${MIGRATION_KEY}`).set({
        completedAt: new Date(),
        documentsUpdated: updatedCount,
        status: 'completed'
      });
      
      console.log('[DocumentLibrary] Evidence to Task Evidence migration completed');
    } catch (error) {
      console.error('[DocumentLibrary] Error during Evidence to Task Evidence migration:', error);
      // Don't throw - we don't want to block the document library from loading
    }
  }, []);

  // Initial load and reload on tenancy change
  useEffect(() => {
    if (!activeTenancy?.id) return;

    // Wait for window.companyID to be updated
    const loadWithDelay = async () => {
      console.log('[DocumentLibrary] Waiting for tenancy switch...');
      // Give time for window.companyID to update
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update window.companyID to match activeTenancy
      window.companyID = activeTenancy.id;
      console.log('[DocumentLibrary] Setting window.companyID:', window.companyID);

      // Initialize notification service with customer ID and user ID
      notificationService.setCustomerID(activeTenancy.id);
      notificationService.setCurrentUserID(user?.uid || null);

      // Run migration before loading documents
      await migrateEvidenceToTaskEvidence(activeTenancy.id);

      await loadDocuments();
      await loadDeletedDocuments();
    };

    loadWithDelay();
  }, [loadDocuments, loadDeletedDocuments, activeTenancy?.id, migrateEvidenceToTaskEvidence]);

  // SharePoint integration functions
  const loadSharePointSites = async () => {
    try {
      setIsLoadingSites(true);
      const sites = await documentsService.getSharePointSites();
      setSharePointSites(sites);
      if (sites.length > 0) {
        setSelectedSite(sites[0]);
        loadSharePointDrives(sites[0].id);
      }
    } catch (error) {
      console.error('Error loading SharePoint sites:', error);
      toast.error('Failed to load SharePoint sites');
    } finally {
      setIsLoadingSites(false);
    }
  };

  const loadSharePointDrives = async (siteId: string) => {
    try {
      setIsLoadingDrives(true);
      const site = sharePointSites.find(s => s.id === siteId);
      if (site && site.drives) {
        setSharePointDrives(site.drives);
        if (site.drives.length > 0) {
          setSelectedDrive(site.drives[0]);
          loadSharePointFiles(site.drives[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading SharePoint drives:', error);
      toast.error('Failed to load SharePoint drives');
    } finally {
      setIsLoadingDrives(false);
    }
  };

  const loadSharePointFiles = async (driveId: string, folderId?: string) => {
    try {
      setIsLoadingFiles(true);
      const files = await documentsService.getSharePointFiles(driveId, folderId);
      setSharePointFiles(files);
    } catch (error) {
      console.error('Error loading SharePoint files:', error);
      toast.error('Failed to load files from SharePoint');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(fileId)) {
        newSelection.delete(fileId);
      } else {
        newSelection.add(fileId);
      }
      return newSelection;
    });
  };

  const importSelectedFiles = async () => {
    if (selectedFiles.size === 0) return;

    try {
      setIsImporting(true);
      const fileIds = Array.from(selectedFiles);
      const results = {
        successful: 0,
        failed: 0,
        total: fileIds.length,
        errors: [] as { fileName: string; error: string }[]
      };

      // Process files one by one to handle errors individually
      for (const fileId of fileIds) {
        // Find the file in the current file list to get its name for better error messages
        const fileInfo = sharePointFiles.find(f => f.id === fileId);

        try {
          // Pull the file using pullSharePointFile which internally uses createDocument
          const result = await documentsService.pullSharePointFile(
            selectedDrive.id,
            fileId,
            {
              documentType: 'Document',
              classification: 'Business Confidential'
            }
          );

          if (result.success) {
            results.successful++;
            toast.success(`Imported: ${result.fileName || 'File'}`);
          } else {
            results.failed++;
            results.errors.push({
              fileName: fileInfo?.name || `File (${fileId})`,
              error: result.message || 'Unknown error'
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            fileName: fileInfo?.name || `File (${fileId})`,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Show summary
      if (results.successful > 0) {
        toast.success(`Successfully imported ${results.successful} of ${results.total} files`);
      }

      if (results.failed > 0) {
        const errorMessage = results.errors.length > 0
          ? `Failed to import ${results.failed} files. First error: ${results.errors[0].error}`
          : `Failed to import ${results.failed} files.`;
        toast.error(errorMessage);
      }

      // Refresh documents after import
      await loadDocuments();

      // Close the modal and reset selection if all files were processed successfully
      if (results.failed === 0) {
        setIsSharePointOpen(false);
        setSelectedFiles(new Set());
      }
    } catch (error) {
      console.error('Error in import process:', error);
      toast.error('An error occurred during the import process');
    } finally {
      setIsImporting(false);
    }
  };

  const authenticateSharePoint = async () => {
    try {
      const tenantId = prompt('Please enter your Microsoft 365 tenant ID:');
      if (!tenantId) return;

      const authUrl = await documentsService.getSharePointAuthUrl(tenantId);
      const popup = window.open(authUrl, '_blank', 'width=600,height=700');

      // Poll to check if popup is closed
      const checkPopup = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkPopup);
          // Check auth status after popup is closed
          const checkAuth = async () => {
            const status = await documentsService.getSharePointAuthStatus();
            setIsAuthenticated(status.isAuthenticated);
            if (status.isAuthenticated) {
              loadSharePointSites();
            }
          };
          checkAuth();
        }
      }, 1000);
    } catch (error) {
      console.error('Error authenticating with SharePoint:', error);
      toast.error('Failed to authenticate with SharePoint');
    }
  };

  // Extract unique values for filters
  const typeOptions = Array.from(new Set((documents || []).map(a => (a?.documentType || a?.docType || a?.type || 'Other'))));

  // Get unique statuses from documents, normalize 'Current' to 'Published' and remove duplicates
  const statusOptions = Array.from(
    new Set(
      (documents || []).map(a => {
        const status = a?.status || 'Current';
        // Normalize 'Current' to 'Published' for display
        return status === 'Current' ? 'Published' : status;
      })
    )
  )
    .filter(status => status !== 'Current') // Remove 'Current' from the filter options
    .filter(Boolean) // Remove any null/undefined values
    .filter((value, index, self) => self.indexOf(value) === index); // Ensure uniqueness

  const ownerOptions = Array.from(new Set((documents || []).map(a => (userIdToName[a?.uploadedBy] || 'Unassigned'))));

  // Filtering
  const filteredDocuments = useMemo(() => {
    const targetDocuments = view === 'active' ? documents : deletedDocuments;

    if (!targetDocuments || !Array.isArray(targetDocuments)) return [];

    return targetDocuments.filter(doc => {
      if (!doc) return false;

      // For deleted documents, we need to check doc.data for the properties
      const docData = view === 'deleted' ? (doc as any).data || {} : doc;
      const docType = docData.documentType || docData.docType || docData.type || 'Other';
      const docStatus = docData.status || 'Current';
      const docOwner = docData.uploadedBy || '';
      const docName = docData.displayName || docData.fileName || docData.name || '';
      const docDescription = docData.description || '';

      // Normalize status for filtering (treat 'Current' as 'Published')
      const normalizedStatus = docStatus === 'Current' ? 'Published' : docStatus;

      const matchesSearch = searchTerm === '' ||
        docName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        docDescription.toLowerCase().includes(searchTerm.toLowerCase());
      // Hide 'evidence' and 'task evidence' documents by default, unless specifically filtered for
      // DED-943: Task Evidence documents should be auto-hidden in document library
      const lowerDocType = docType.toLowerCase();
      const isEvidence = lowerDocType === 'evidence';
      const isTaskEvidence = lowerDocType === 'task evidence';
      const matchesType = typeFilter === 'all'
        ? !isEvidence && !isTaskEvidence // hide evidence and task evidence by default
        : lowerDocType === typeFilter.toLowerCase();
      const matchesStatus = statusFilter === 'all' ? true : normalizedStatus === statusFilter;
      const matchesOwner = ownerFilter === 'all' ? true : (userIdToName[docOwner] || 'Unassigned') === ownerFilter;

      return matchesSearch && matchesType && matchesStatus && matchesOwner;
    });
  }, [searchTerm, typeFilter, statusFilter, ownerFilter, documents, deletedDocuments, view, userIdToName]);

  // Sorting logic placeholder
  const [sortKey, setSortKey] = useState<keyof Document>('category');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Handle bulk edit save
  const handleBulkEditSave = async (updatedDocuments: Document[], fileToUpload: File | null = null) => {
    try {
      // Show loading indicator
      setIsLoading(true);

      // Create a map of updated documents by ID for quick lookup
      const updatedDocsMap = new Map(updatedDocuments.map(doc => [doc.docID, doc]));

      // Save each document to the database directly
      const savePromises = updatedDocuments.map(async (updatedDoc) => {
        try {
          // Get the existing document from the service
          const existingDoc = await getDocument(updatedDoc.docID);
          if (!existingDoc) {
            throw new Error(`Document ${updatedDoc.docID} not found`);
          }

          // Update document properties directly on the document instance
          // These are the fields that can be updated in bulk edit
          existingDoc.fileName = updatedDoc.fileName;
          existingDoc.status = updatedDoc.status;
          existingDoc.classification = updatedDoc.classification;
          existingDoc.comments = updatedDoc.comments;
          existingDoc.confluenceURL = updatedDoc.confluenceURL;
          existingDoc.owner = updatedDoc.owner;
          existingDoc.uploadedBy = updatedDoc.uploadedBy;

          // Special handling for date fields to ensure they're in the correct format
          // Convert date objects or string dates to proper format for Firebase
          if (updatedDoc.lastReviewDate) {
            // Type-safe check for __time__ property using hasOwnProperty
            if (typeof updatedDoc.lastReviewDate === 'object' &&
              updatedDoc.lastReviewDate !== null &&
              Object.prototype.hasOwnProperty.call(updatedDoc.lastReviewDate, '__time__')) {
              // If it's in the {__time__: '...'} format, convert to a Date
              const timeStr = (updatedDoc.lastReviewDate as any).__time__;
              existingDoc.lastReviewDate = new Date(timeStr);
            } else if (typeof updatedDoc.lastReviewDate === 'string') {
              // If it's a string, convert to a Date
              existingDoc.lastReviewDate = new Date(updatedDoc.lastReviewDate);
            } else {
              // Otherwise use as is (could be a Timestamp already)
              existingDoc.lastReviewDate = updatedDoc.lastReviewDate;
            }
          }

          if (updatedDoc.nextReviewDate) {
            // Type-safe check for __time__ property using hasOwnProperty
            if (typeof updatedDoc.nextReviewDate === 'object' &&
              updatedDoc.nextReviewDate !== null &&
              Object.prototype.hasOwnProperty.call(updatedDoc.nextReviewDate, '__time__')) {
              // If it's in the {__time__: '...'} format, convert to a Date
              const timeStr = (updatedDoc.nextReviewDate as any).__time__;
              existingDoc.nextReviewDate = new Date(timeStr);
            } else if (typeof updatedDoc.nextReviewDate === 'string') {
              // If it's a string, convert to a Date
              existingDoc.nextReviewDate = new Date(updatedDoc.nextReviewDate);
            } else {
              // Otherwise use as is (could be a Timestamp already)
              existingDoc.nextReviewDate = updatedDoc.nextReviewDate;
            }
          }

          existingDoc.procRef = updatedDoc.procRef;
          existingDoc.documentType = updatedDoc.documentType;
          existingDoc.componentId = updatedDoc.componentId;
          existingDoc.edit = true;
          existingDoc.updateMethod = updatedDocuments.length <= 1 ? 'edit' : 'bulkedit';
          console.log('Updating document:', existingDoc);

          // First update the document's metadata
          await existingDoc.update();

          // If we have a file to upload and this is the document that should receive it
          // (in single document mode, there should be only one document in the array)
          if (fileToUpload && updatedDocuments.length === 1 && updatedDoc.docID === updatedDocuments[0].docID) {
            console.log(`Uploading file for document ${updatedDoc.docID}:`, fileToUpload.name);
            try {
              // Update fileName to the actual uploaded file name (not display name)
              existingDoc.fileName = fileToUpload.name;
              // Clear confluence URL if replacing with a file upload
              existingDoc.confluenceURL = undefined;
              // Use the saveDocument function which handles file uploads properly
              await saveDocument(existingDoc, fileToUpload);
              console.log(`File uploaded successfully for document ${updatedDoc.docID}`);
            } catch (fileError) {
              console.error(`Failed to upload file for document ${updatedDoc.docID}:`, fileError);
              // Still return success if the document update succeeded but file upload failed
              return {
                success: true,
                docId: updatedDoc.docID,
                warning: 'Document was updated but file upload failed'
              };
            }
          } else {
            console.log(`Document ${updatedDoc.docID} updated successfully without file upload`);
          }

          return { success: true, docId: updatedDoc.docID };
        } catch (error) {
          console.error(`Error updating document ${updatedDoc.docID}:`, error);
          return { success: false, docId: updatedDoc.docID, error };
        }
      });

      // Wait for all save operations to complete
      const results = await Promise.all(savePromises);

      // Check for any failures or warnings
      const failures = results.filter(result => !result.success);
      const warnings = results.filter(result => result.success && result.warning);

      if (failures.length > 0) {
        console.error(`Failed to update ${failures.length} documents:`, failures);
        toast.error(`Some updates failed. ${results.length - failures.length} of ${results.length} documents were updated successfully.`);
      } else if (warnings.length > 0) {
        console.warn(`${warnings.length} documents updated with warnings:`, warnings);
        toast.warning(`${results.length} document${results.length !== 1 ? 's' : ''} updated but some actions had warnings.`);
      } else {
        toast.success(`Successfully updated ${results.length} document${results.length !== 1 ? 's' : ''}.`);
      }

      // Update the documents array - reload documents to get fresh data
      try {
        // Get fresh documents from the service
        const freshDocs = await getDocuments();

        if (freshDocs && Array.isArray(freshDocs)) {
          // Deduplicate documents to prevent duplicates in the table
          const uniqueDocsMap = new Map();

          // Add each document to the map, with the ID as the key
          freshDocs.forEach(doc => {
            if (doc && doc.docID) {
              uniqueDocsMap.set(doc.docID, doc);
            }
          });

          // Convert the Map back to an array
          const uniqueDocs = Array.from(uniqueDocsMap.values());
          console.log('Refreshed documents after update:', uniqueDocs.length);

          // Update state with deduplicated documents
          setDocuments(uniqueDocs);
        }
      } catch (refreshError) {
        console.error('Error refreshing documents:', refreshError);
        // Fall back to local update if refresh fails
        const newDocuments = documents.map(doc => {
          return updatedDocsMap.has(doc.docID) ? updatedDocsMap.get(doc.docID)! : doc;
        });
        setDocuments(newDocuments);
      }

      setSelectedDocuments([]);
    } catch (error) {
      console.error('Error in bulk edit save:', error);
      toast.error('There was a problem updating the documents. Please try again.');
    } finally {
      // Hide loading indicator
      setIsLoading(false);
    }
  };
  const sortedDocuments = useMemo(() => {
    let docs= [...filteredDocuments].sort((a, b) => {
      if (a[sortKey] < b[sortKey]) return sortDir === 'asc' ? -1 : 1;
      if (a[sortKey] > b[sortKey]) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    console.log('sortedDocuments', docs);
    return docs;
  }, [filteredDocuments, sortKey, sortDir]);

  const handleSort = (key: keyof Document) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-primary border-primary-fade-border'
      : 'border border-border bg-transparent text-foreground';

  useEffect(() => {
    getHelpContentByPage('document.library').then(setHelpContent);
  }, []);

  // Tour implementation
  useEffect(() => {
    setTourPage('document-library');
  }, [setTourPage]);

  // Tour configuration for Document Library
  useEffect(() => {
    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">Document Library</h3>
            <p>This is your document library page. It shows you all the documents in your tenant.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.DOCUMENT_LIBRARY_PAGE,
        position: "bottom" as const,
      }
    ];

    // Filter buttons
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Active/Deleted Documents filter buttons</h3>
          <p>This will filter the documents by active or deleted.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_FILTER_BUTTONS,
      position: "bottom" as const,
    });

    // Add Document button
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Add Document button</h3>
          <p>This will open the add document modal.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_ADD_BUTTON,
      position: "bottom" as const,
    });

    // Import from SharePoint
    if (getAvailableFeatures().find(f => f.name === 'SharePoint')) {
      steps.push({
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">Import from SharePoint</h3>
            <p>This will import documents from SharePoint.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.DOC_IMPORT_SHAREPOINT,
        position: "bottom" as const,
      });
    }

    // Search bar
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Search</h3>
          <p>This will search for documents.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_SEARCH_BAR,
      position: "bottom" as const,
    });

    // Filter boxes
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Filter Boxes</h3>
          <p>This will filter the documents.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_FILTER_BOXES,
      position: "bottom" as const,
    });

    // Sort table
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Sort table</h3>
          <p>This will sort the table by the selected column.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_SORTING_BUTTONS,
      position: "bottom" as const,
    });

    // Table action buttons
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Table action buttons</h3>
          <p>These buttons allow you to view, download, or suggest tasks for documents.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_TABLE_ACTION_BUTTONS,
      position: "bottom" as const,
    });

    // Per page
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Per page</h3>
          <p>This will change the number of results per page.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_RESULTS_SELECTOR,
      position: "left" as const,
    });

    // Columns button
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Columns button</h3>
          <p>This will open the columns modal.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.DOC_MANAGE_COLUMNS,
      position: "left" as const,
    });

    console.log('Document Library tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted && documents.length > 0) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage, documents, features]);

  return (
    <div id={TOUR_STEP_IDS.DOCUMENT_LIBRARY_PAGE} className="page-container">
      <div className="glass-card">
        <div className="header-row flex gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="title flex flex-row">
              Document Library
              <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              This is your central hub for managing all key
              documentation related to your governance, risk, and
              compliance.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 md:flex-nowrap">
            {selectedDocuments.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                onClick={() => setIsBulkEditOpen(true)}
              >
                <FileEdit className="h-4 w-4" />
                Bulk Edit ({selectedDocuments.length})
              </Button>
            )}
            {/* View Toggle */}

            <div id={TOUR_STEP_IDS.DOC_FILTER_BUTTONS} className="flex gap-2">
              <Button
                onClick={() => setView('active')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'active'
                  ? 'filter-btn-active border font-semibold bg-primary-fade text-primary border-primary-fade-border'
                  : 'bg-background text-muted-foreground hover:text-white'
                  }`}
              >
                Active Documents
              </Button>
              {!isReadOnly && (
                <Button
                  onClick={() => setView('deleted')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'deleted'
                    ? 'filter-btn-active border font-semibold bg-primary-fade text-primary border-primary-fade-border'
                    : 'bg-background text-muted-foreground hover:text-white'
                    }`}
                >
                  Deleted Documents
                </Button>
              )}
            </div>
            {!isReadOnly && (
              getAvailableFeatures().find(f => f.name === 'Template Library') ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      id={TOUR_STEP_IDS.DOC_ADD_BUTTON}
                      className="primary-btn"
                      size="sm"
                      style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Document
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[220px]">
                    <DropdownMenuItem onClick={() => navigate('/documentdetail/new')}>
                      <span className="ml-0">Upload Document</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/template-library')}>
                      <span className="ml-0">Select Template</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  id={TOUR_STEP_IDS.DOC_ADD_BUTTON}
                  className="primary-btn"
                  size="sm"
                  style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
                  onClick={() => navigate('/documentdetail/new')}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Document
                </Button>
              )
            )}
            {getAvailableFeatures().find(f => f.name === 'SharePoint') && !isReadOnly && (
              <Button
                id={TOUR_STEP_IDS.DOC_IMPORT_SHAREPOINT}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setIsSharePointOpen(true)}
              >
                <Share2 className="h-4 w-4" />
                <span className="hidden sm:inline">Import from SharePoint</span>
                <span className="sm:hidden">SharePoint</span>
              </Button>
            )}
            {/* Dynamic Integration Hooks for document-import slot */}
            {!isReadOnly && (
              <IntegrationSlot
                slot="document-import"
                buttonVariant="outline"
                buttonSize="sm"
                onActionComplete={(integrationId, result: any) => {
                  // Handle imported file
                  if (result?.file && activeTenancy?.id) {
                    const newDoc = {
                      fileName: result.file.name,
                      displayName: result.item?.title || result.pageTitle || result.file.name,
                      documentType: 'Policy',
                      status: 'Draft',
                      version: '1.0',
                      description: `Imported from ${integrationId}: ${result.item?.title || result.pageTitle || ''}`,
                      uploadedBy: user?.uid || '',
                      dateUploaded: new Date(),
                      nextReviewDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year from now
                      classification: 'Business Confidential', // Add required classification
                      metadata: {
                        // Store integration source metadata for refresh functionality
                        integrationId,
                        confluencePageId: result.item?.id || result.pageId,
                        confluenceSpaceKey: result.item?.spaceKey,
                        lastRefreshed: new Date().toISOString(),
                      },
                    };
                    createDocument(newDoc as any, result.file, activeTenancy.id)
                      .then(() => {
                        toast.success(`Imported "${result.item?.title || result.pageTitle || result.file.name}" successfully`);
                        loadDocuments();
                      })
                      .catch((err: any) => {
                        toast.error(`Failed to save: ${err.message}`);
                      });
                  }
                }}
              />
            )}
          </div>
        </div>

        <div className="filters-row flex-col md:flex-row md:items-center md:gap-4 md:justify-between my-5">
          {/* Search and Filters */}
          <div className="flex-1">
            <InputWithIcon
              id={TOUR_STEP_IDS.DOC_SEARCH_BAR}
              icon={<Search className="h-4 w-4 text-gray-400" />}
              placeholder={view === 'active' ? 'Search documents...' : 'Search deleted documents...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-lg"
            />
          </div>
          {view === 'deleted' && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <History className="h-4 w-4 mr-1" />
              <span>Deleted documents are kept for 30 days</span>
            </div>
          )}

          {/* Filter Controls */}
          <div className={`filter-controls ${isMobile ? 'mt-3' : 'mt-0'} flex flex-wrap gap-2 justify-center md:justify-end md:w-auto`}>
            {/* All Documents Button */}
            <Button
              variant="outline"
              size="sm"
              className={getFilterButtonClass(typeFilter === 'all' && statusFilter === 'all' && ownerFilter === 'all') + ' h-9 px-4 rounded-(--radius) flex items-center gap-2'}
              onClick={() => {
                setTypeFilter('all');
                setStatusFilter('all');
                setOwnerFilter('all');
              }}
            >
              All Documents
            </Button>

            {/* Document Type Dropdown */}
            {/* DED-943: Added Task Evidence filter option with help text */}
            <div id={TOUR_STEP_IDS.DOC_FILTER_BOXES} className="flex gap-2 flex-wrap">
              <Select value={typeFilter} onValueChange={v => setTypeFilter(v as typeof typeFilter)}>
                <SelectTrigger className={getFilterButtonClass(typeFilter !== 'all') + ' min-w-[120px] rounded-(--radius) flex items-center gap-2'}>
                  <span>
                    {typeFilter !== 'all' ? `Type: ${typeFilter}` : 'Type'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {typeOptions
                    .map(option => (
                      <SelectItem key={option as string} value={option as string}>{option as string}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {/* Status Dropdown */}
              <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
                <SelectTrigger className={getFilterButtonClass(statusFilter !== 'all') + ' min-w-[120px] rounded-(--radius) flex items-center gap-2'}>
                  <span>
                    {statusFilter !== 'all' ? `Status: ${statusFilter}` : 'Status'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {statusOptions.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Owner Dropdown */}
              <Select value={ownerFilter} onValueChange={v => setOwnerFilter(v as typeof ownerFilter)}>
                <SelectTrigger className={getFilterButtonClass(ownerFilter !== 'all') + ' min-w-[120px] rounded-(--radius) flex items-center gap-2'}>
                  <span>
                    {ownerFilter !== 'all' ? `Owner: ${ownerFilter}` : 'Owner'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {ownerOptions.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>


        {/* Bulk Edit Sheet */}
        <BulkEditSheet
          isOpen={isBulkEditOpen}
          onOpenChange={(open) => {
            setIsBulkEditOpen(open);
            if (!open) setDocumentToEdit(null);
          }}
          selectedDocuments={documentToEdit ? [documentToEdit] : selectedDocuments}
          onSave={handleBulkEditSave}
          singleDocumentMode={!!documentToEdit}
        />

        {/* Responsive Document List: DataTable for desktop, Cards for mobile */}
        <div className="document-list">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-muted-foreground">Loading documents...</p>
              </div>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex flex-col items-center gap-2">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <p className="text-muted-foreground">No documents found</p>
              </div>
            </div>
          ) : (
            <>
              {/* DED-943: Task Evidence hidden notice - top right of table */}
              {typeFilter === 'all' && (
                <div className="flex justify-end mb-2">
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <HelpPopoverHover config={{ 
                      style: 'underline',
                      text: 'Documents uploaded as evidence during task performance are hidden by default. Use the Type filter and select "Task Evidence" to view them.',
                      cursor: true
                    }}>
                      <span>Task Evidence hidden</span>
                    </HelpPopoverHover>
                  </span>
                </div>
              )}
              <div className="hidden md:block">
                <DataTable
                  sortingId={TOUR_STEP_IDS.DOC_SORTING_BUTTONS}
                  columns={getDocumentColumns({
                    viewMode: view,
                    userIdToName: userIdToName,
                    helpData: helpContent,
                    isReadOnly,
                    onEdit: (row) => {
                      setDocumentToEdit(row);
                      setIsBulkEditOpen(true);
                    },
                    onGenerateAITasks: (row) => {
                      handleGenerateAITasks(row);
                    },
                    onView: (row) => {
                      if (isReadOnly) {
                        // Navigate to auditor document detail view when in read-only mode
                        const id = row?.componentId || row.docID;
                        console.log(`Navigating to auditor document detail: /auditor-view/document-detail/${id}`);
                        navigate(`/auditor-view/document-detail/${id}`);
                      } else {
                        // Navigate to regular document detail view
                        navigate(`/documentdetail/${row?.componentId || row.docID}`);
                      }
                    },
                    onRecover: async (row) => {
                      handleRecoverDocument(row);
                    },
                    onDownload: async (row) => {
                      console.log('[DocumentLibrary] Downloading document...', row);
                      if (!row) return;

                      try {
                        // Get the document ID for lookup
                        const docId = row?.componentId || row.docID || row.id;
                        const numericDocId = docId?.toString().replace('SUP-', '') || docId;

                        if (!docId) {
                          toast.error('Document ID not found');
                          return;
                        }

                        // Get the full document instance from the service
                        const docInstance = await getDocument(docId.toString());

                        if (!docInstance) {
                          toast.error('Document not found');
                          return;
                        }

                        // Now we can call download on the proper document instance
                        await docInstance.download();
                        toast.success('Document downloaded successfully');
                      } catch (error: any) {
                        console.error('[DocumentLibrary] Download error:', error);

                        // Check if it's a file not found error
                        const isNotFound = error?.code === 'storage/object-not-found' || 
                          error?.message?.includes('does not exist') ||
                          error?.message?.includes('404');

                        // Store original error for later use
                        const originalError = error;

                        if (isNotFound) {
                          // Try to list files in the folder and show picker
                          console.log('[DocumentLibrary] File not found, attempting to list files in folder...');
                          const searchingToast = toast.loading('Searching for available files...');
                          
                          try {
                            const { ref, listAll, getMetadata } = await import('firebase/storage');
                            const { storage } = await import('@/lib/firebase');
                            
                            const fileDocId = row?.componentId || row.docID || row.id;
                            const numericDocId = fileDocId?.toString().replace('SUP-', '') || fileDocId;
                            
                            // Try to list files in the keyDocuments folder for this docId
                            const folderPath = `customers/${activeTenancy?.id}/keyDocuments/otherDocs/uploaded/${numericDocId}`;
                            
                            let foundFiles: Array<{ name: string; fullPath: string; size?: number; updated?: Date }> = [];
                            
                            try {
                              const folderRef = ref(storage, folderPath);
                              const result = await listAll(folderRef);
                              
                              if (result.items.length > 0) {
                                console.log(`[DocumentLibrary] Found ${result.items.length} files in ${folderPath}`);
                                
                                // Get metadata for each file
                                for (const item of result.items) {
                                  try {
                                    const metadata = await getMetadata(item);
                                    foundFiles.push({
                                      name: item.name,
                                      fullPath: item.fullPath,
                                      size: metadata.size,
                                      updated: metadata.updated ? new Date(metadata.updated) : undefined,
                                    });
                                  } catch (metaError) {
                                    // If we can't get metadata, still add the file
                                    foundFiles.push({
                                      name: item.name,
                                      fullPath: item.fullPath,
                                    });
                                  }
                                }
                              }
                            } catch (listError) {
                              console.log(`[DocumentLibrary] Could not list files in ${folderPath}:`, listError);
                            }
                            
                            toast.dismiss(searchingToast);
                            
                            if (foundFiles.length > 0) {
                              // Show file picker dialog
                              setFilePickerFiles(foundFiles);
                              setFilePickerDocName((row as any).displayName || (row as any).fileName || 'Document');
                              setFilePickerDocId(numericDocId);
                              setFilePickerRow(row);
                              setFilePickerOpen(true);
                            } else {
                              toast.error('File not found and no alternative files available in storage');
                            }
                          } catch (listError) {
                            toast.dismiss(searchingToast);
                            console.error('[DocumentLibrary] Error listing files:', listError);
                            toast.error('File not found in storage');
                          }
                        } else {
                          // Get a meaningful error message
                          const errorMessage = originalError instanceof Error
                            ? originalError.message
                            : 'Failed to download document. The file may not exist.';

                          toast.error(errorMessage);
                        }
                      }
                    }
                  }, features)}
                  data={sortedDocuments}
                  pageIndex={pageIndex}
                  onPageIndexChange={setPageIndex}
                  columnDisplayConfig={documentLibraryColumnConfig}
                  selectable
                  onRowSelectionChange={setSelectedDocuments}
                  pageSize={20}
                  onRowClick={(row) => {
                    if (isReadOnly) {
                      const id = row?.componentId || row.docID;
                      navigate(`/auditor-view/document-detail/${id}`);
                    } else {
                      navigate(`/documentdetail/${row?.componentId || row.docID}`);
                    }
                  }}
                />
              </div>
              <div className="block md:hidden">
                {/* Mobile Cards */}
                {sortedDocuments.map(document => (
                  <div
                    className="mobile-card cursor-pointer hover:shadow-lg transition-shadow"
                    key={document?.componentId || document?.docID}
                    style={{
                      backgroundColor: theme.isDark ? '#18181b' : 'white',
                      color: theme.isDark ? '#f3f4f6' : '#18181b',
                      border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
                      boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                    onClick={() => navigate(`/documentdetail/${document?.componentId || document?.docID}`)}
                    title="View Document"
                  >
                    <div className="mobile-card-header flex justify-between items-center">
                      <span className="mobile-card-id" data-force-isms-override="true">Document: {(document as any).displayName || document.fileName}</span>
                      <button
                        className="ml-2 p-1 rounded hover:bg-primary/10"
                        onClick={e => { e.stopPropagation(); navigate(`/documentdetail/${document?.componentId || document?.docID}`); }}
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mobile-card-field"><strong>Type:</strong> <Badge variant={getDocTypeVariant(document?.documentType || 'Other') as any}>{document?.documentType || 'Other'}</Badge></div>
                    <div className="mobile-card-field"><strong>Owner:</strong> {document?.owner || 'Deleted or unknown user'}</div>
                    <div className="mobile-card-field"><strong>Status:</strong>
                      <Badge variant={getStatusVariant(document?.status || 'Current') as any}>
                        <span className="inline-block w-2 h-2 rounded-full mr-1 bg-current opacity-70"></span>
                        {document?.status || 'Current'}
                      </Badge>
                    </div>
                    <div className="mobile-card-field"><strong>Review Date:</strong> {
                      (() => {
                        const date = document?.nextReviewDate;
                        let formattedDate;

                        if (date) {
                          // Use type guards to handle different date formats
                          if (typeof date === 'object' && date !== null) {
                            // Handle object types
                            const dateObj = date as any; // Use any for property access

                            if (dateObj.toDate && typeof dateObj.toDate === 'function') {
                              // Firestore Timestamp-like object
                              formattedDate = renderDate(dateObj.toDate());
                            } else if (dateObj.__time__ && typeof dateObj.__time__ === 'string') {
                              // Object with __time__ property
                              formattedDate = renderDate(new Date(dateObj.__time__));
                            } else {
                              // Try to use it directly
                              try {
                                formattedDate = renderDate(dateObj);
                              } catch (e) {
                                formattedDate = '-';
                              }
                            }
                          } else if (typeof date === 'string') {
                            // Handle string date
                            try {
                              formattedDate = renderDate(new Date(date));
                            } catch (e) {
                              formattedDate = date;
                            }
                          }
                        }

                        return renderDate(formattedDate) || 'Not Set';
                      })()
                    }</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* SharePoint Import Modal */}
      <Dialog open={isSharePointOpen} onOpenChange={setIsSharePointOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col bg-card">
          <DialogHeader>
            <DialogTitle>Import from SharePoint</DialogTitle>
            <DialogDescription>
              Browse and import documents from your SharePoint sites
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col">
            {!isAuthenticated ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Share2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Connect to SharePoint</h3>
                <p className="text-muted-foreground mb-6">
                  Sign in to your Microsoft 365 account to access your SharePoint files
                </p>
                <Button onClick={authenticateSharePoint}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Sign in with Microsoft 365
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Site</label>
                    <select
                      className="w-full p-2 border rounded-md bg-background"
                      value={selectedSite?.id}
                      onChange={(e) => {
                        const site = sharePointSites.find(s => s.id === e.target.value);
                        if (site) {
                          setSelectedSite(site);
                          loadSharePointDrives(site.id);
                        }
                      }}
                      disabled={isLoadingSites || sharePointSites.length === 0}
                    >
                      {sharePointSites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.displayName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Library</label>
                    <select
                      className="w-full p-2 border rounded-md bg-background"
                      value={selectedDrive?.id}
                      onChange={(e) => {
                        const drive = sharePointDrives.find(d => d.id === e.target.value);
                        if (drive) {
                          setSelectedDrive(drive);
                          loadSharePointFiles(drive.id);
                        }
                      }}
                      disabled={isLoadingDrives || sharePointDrives.length === 0}
                    >
                      {sharePointDrives.map(drive => (
                        <option key={drive.id} value={drive.id}>
                          {drive.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="border rounded-md flex-1 overflow-auto">
                  {isLoadingFiles ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      <span className="ml-2">Loading files...</span>
                    </div>
                  ) : sharePointFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                      <FileText className="h-8 w-8 mb-2" />
                      <p>No files found in this library</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {sharePointFiles.map(file => (
                        <div
                          key={file.id}
                          className={`p-3 hover:bg-accent cursor-pointer flex items-center ${selectedFiles.has(file.id) ? 'bg-accent' : ''}`}
                          onClick={() => handleFileSelect(file.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedFiles.has(file.id)}
                            onChange={() => handleFileSelect(file.id)}
                            className="mr-3 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{file.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(file.lastModifiedDateTime).toLocaleDateString()} •
                              {Math.round(file.size / 1024)} KB
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsSharePointOpen(false);
                setSelectedFiles(new Set());
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={importSelectedFiles}
              disabled={selectedFiles.size === 0 || isImporting}
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                `Import ${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {currentProcessingDoc && (
        <AITaskProcessingDialog
          isOpen={aiDialogOpen}
          fileName={currentProcessingDoc.fileName}
          onClose={() => {
            setAiDialogOpen(false);
            setProcessingAITask(null);
            setCurrentProcessingDoc(null);
          }}
        />
      )}

      {/* File Picker Dialog (for when download file not found) */}
      <FilePickerDialog
        open={filePickerOpen}
        onOpenChange={setFilePickerOpen}
        files={filePickerFiles}
        policyName={filePickerDocName}
        docId={filePickerDocId}
        onSelectFile={handleFilePickerSelect}
        isLoading={filePickerLoading}
      />
      <TourAlertDialog 
        isOpen={openTour} 
        setIsOpen={setOpenTour} 
      />
    </div>
  );
};

export default DocumentLibrary;
